import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MessageSquare, Plus } from 'lucide-react';
import { InquiryButton, ThreadPanel } from '@speles7172/message-console';
import { DocumentPreviewModal } from '../expenses/ExpenseDocumentsSection';
import type { PreviewableDocument } from '../documents/types';
import '@speles7172/message-console/styles.css';
import { useAuth } from '../../context/AuthContext';
import {
  createRecordTransport,
  fetchRecordThreadId,
  fetchRecordThreads,
  type RecordThread,
} from '../../api/notifications';
import { fetchAttachmentUrl, fetchThreadContext, type ThreadContext } from '../../api/threadTools';
import { checkGrammar } from '../../api/ai';
import { EntityModal } from '../common/EntityModal';
import { ThreadTools } from './ThreadTools';
import type { ThreadUploadLinkInput } from '../../api/threadTools';
import { NewConversationModal } from './NewConversationModal';

/**
 * The conversations hanging off one record (COMMUNICATIONS-DESIGN D7).
 *
 * Drop it on an event, a vendor, a venue or an expense and that record has
 * inquiries. Nothing about it is per-domain — `entityType` and `entityId` are
 * passed straight through and neither this component nor the package ever
 * interprets them — so a new record type is one more usage, not one more
 * component.
 *
 * ⚠️ **`<InquiryButton>` fetches per button**, which is right for a detail page
 * and wrong for a list: forty rows are forty requests. So a list passes
 * `count`, fetched once for the whole page with `fetchThreadCounts`, and this
 * renders its own button instead — no request until somebody opens one.
 *
 * That distinction was in this comment before it was in the code: the enquiries
 * page mounted the per-button version on every row.
 *
 * ⚠️ **The panel is the package's, wrapped in `.peles-controls`.** A
 * conversation with its transcript, its participants, its status and its
 * composer is exactly the sort of thing worth adopting rather than rebuilding,
 * and the wrapper is Phase A's bridge so it wears this application's palette.
 * The composer here is the package's plain one — the rich-text composer with
 * its own assistant panel belongs to email, where a draft is a document and a
 * thread message is a remark. It does get the **grammar pass**, though, through
 * the hook the package leaves for it: the same `POST /ai/grammar-check` Phase B
 * built, so a message to a vendor is checked by the model an administrator
 * chose, without a second implementation.
 */
export interface RecordConversationsProps {
  entityType: string;
  entityId: string;
  /** What the button says. Defaults to the package's wording. */
  label?: string;
  /**
   * This record's thread counts, when the page fetches them in bulk.
   *
   * Supplying it swaps the package's self-fetching button for one that costs
   * nothing until clicked — which is what makes this safe to put on a list.
   *
   * ⚠️ **`null` is not zero.** A page whose counts are still loading, or whose
   * request failed, knows it owns the badge and does not yet know the number;
   * rendering that as `0` states a conversation does not exist, which is a
   * worse answer than showing no badge at all. So `null` means "mine, unknown"
   * and `undefined` means "not mine — fetch for yourself".
   */
  count?: { total: number; unread: number } | null;
  /**
   * How it renders.
   *
   * `'button'` is the labelled control for a detail page or a card.
   * `'icon'` is a single glyph for a table row, where a button reading
   * "Conversation" would take a column of its own on a line that has none to
   * spare — and where the thread must open in a modal rather than unfold
   * underneath, because a row cannot grow without shoving the table apart.
   */
  variant?: 'button' | 'icon';
}

/**
 * How many the dropdown asks for at a time.
 *
 * A `<select>` is the right control for a handful and the wrong one for a
 * hundred, so it opens on the most recently active and grows on request. It is
 * a page size, not a cap: a record with more conversations than this is walked,
 * never truncated.
 */
const SWITCHER_LIMIT = 50;

/**
 * A later page appended to the ones already listed, keeping the server's order.
 *
 * ⚠️ **By id, because the order is last activity first.** A conversation that
 * gains a message while somebody is paging moves to the head and comes back on
 * a page it was not on before; appending blind would list it twice and give the
 * `<select>` two options that open the same thread.
 */
function mergeThreads(current: RecordThread[], incoming: RecordThread[]): RecordThread[] {
  const seen = new Set(current.map((thread) => thread.id));
  return [...current, ...incoming.filter((thread) => !seen.has(thread.id))];
}

export function RecordConversations({
  entityType,
  entityId,
  label,
  count,
  variant = 'button',
}: RecordConversationsProps) {
  const [openThreadId, setOpenThreadId] = useState<string | null>(null);
  const [opening, setOpening] = useState(false);
  /**
   * Where this conversation's files live, once one is open.
   *
   * ⚠️ **Answered by the server, not derived here.** An expense's files are the
   * *event's* files, and the walk from one to the other is a fact about the
   * schema — see `messages/attachments.ts`. A browser guessing it would be a
   * second copy of that rule, free to disagree about which folder a file went
   * into.
   */
  const [context, setContext] = useState<ThreadContext | null>(null);
  /**
   * Bumped whenever the toolbar puts something on the thread.
   *
   * ⚠️ **A remount, because `<ThreadPanel>` exposes no reload.** It loads its
   * own attachments and hands back no handle, so a file attached beside it
   * would sit on the server and not on the screen until somebody closed and
   * reopened the conversation. Remounting a transcript costs one fetch; the
   * alternative was rebuilding the panel here out of `useThread` to reach
   * `reload()`, which is a whole screen this application would then own.
   */
  const [reloadKey, setReloadKey] = useState(0);
  /**
   * Every conversation this record has, for the switcher.
   *
   * ⚠️ **Loaded only once one is open.** A list page renders forty of these and
   * the whole point of taking `count` from the page is that none of them costs
   * a request until somebody clicks. Fetching the roster on mount would undo
   * that for a switcher nobody is looking at.
   */
  const [threads, setThreads] = useState<RecordThread[]>([]);
  /**
   * Where the next page starts, or `null` when the record is exhausted.
   *
   * ⚠️ **Counted off the server's window, never off the rows on screen.**
   * Per-row admission narrows a page without changing where the next one
   * starts, so a client advancing by `threads.length` would re-ask for
   * conversations it had already been refused and never reach the ones past
   * them. The server says *whether* another page exists and never how many the
   * record holds — a count would be a count of conversations this reader is
   * being kept out of.
   */
  const [nextOffset, setNextOffset] = useState<number | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const { user } = useAuth();

  /**
   * One page of this record's conversations.
   *
   * `offset === 0` replaces what is on screen; anything further appends, so
   * "Load more" walks the record instead of re-fetching its head. Rows merge by
   * id because the order is last-activity-first: a conversation that gains a
   * message mid-walk slides forward and would otherwise be listed twice.
   */
  const loadPage = useCallback(
    (offset: number) =>
      fetchRecordThreads(entityType, entityId, { limit: SWITCHER_LIMIT, offset })
        .then((page) => {
          setThreads((current) => (offset === 0 ? page.items : mergeThreads(current, page.items)));
          setNextOffset(page.hasMore ? page.offset + page.limit : null);
        })
        // A switcher that cannot load is a switcher that is not offered. The
        // conversation itself reads and replies regardless — and a failed
        // *later* page keeps both the rows already on screen and the button
        // that asked for them, so a network blip costs a retry rather than the
        // rest of the record.
        .catch(() => {
          if (offset === 0) {
            setThreads([]);
            setNextOffset(null);
          }
        }),
    [entityType, entityId],
  );

  const loadThreads = useCallback(() => {
    void loadPage(0);
  }, [loadPage]);

  /*
   * ⚠️ **Memoised on the two strings, not written inline.** Every component in
   * `message-console` keys its effects on the transport's identity, so an object
   * literal in JSX is a new transport every render and therefore an endless
   * reload — the rule `api/notifications.ts` is a module constant for. A scoped
   * transport cannot be a module constant, so the rule lands here.
   *
   * `canUpload` is what draws the package's dropzone, and it waits for the
   * context: a conversation whose record has no folder has nowhere to file an
   * upload, and a dropzone that answers 400 is worse than none.
   */
  /** A file request waiting for the next message to carry it. */
  const [stagedUpload, setStagedUpload] = useState<ThreadUploadLinkInput | null>(null);

  const transport = useMemo(
    () =>
      createRecordTransport(
        { entityType, entityId },
        {
          canUpload: context?.nodeId != null,
          ...(openThreadId ? { threadId: openThreadId } : {}),
          uploadRequest: stagedUpload,
          onUploadRequestSent: () => setStagedUpload(null),
        },
      ),
    [entityType, entityId, context?.nodeId, openThreadId, stagedUpload],
  );

  useEffect(() => {
    if (openThreadId === null) {
      setContext(null);
      setThreads([]);
      setNextOffset(null);
      return;
    }
    loadThreads();
    let live = true;
    fetchThreadContext(openThreadId)
      .then((resolved) => {
        if (live) setContext(resolved);
      })
      // A conversation whose scope cannot be resolved still reads and replies;
      // it just offers no folder-backed controls. Failing the panel over the
      // toolbar would be the tail wagging the dog.
      .catch(() => {
        if (live) setContext(null);
      });
    return () => {
      live = false;
    };
  }, [openThreadId, loadThreads]);

  const [preview, setPreview] = useState<PreviewableDocument | null>(null);

  /*
   * ⚠️ **The application's viewer, not a new tab.** A conversation's files are
   * the same expense documents and reports every other screen previews in
   * `DocumentPreviewModal`, and a tab is a worse version of it: it loses the
   * conversation behind it, is what a pop-up blocker eats, and on a phone
   * replaces the app. The presigned URL is fetched the same way either way —
   * only where it is rendered changes.
   */
  /*
   * ⚠️ **The viewer belongs to the conversation that opened it.** Two ways it
   * outlived one: closing the conversation left the file on screen with nothing
   * behind it, and a URL that resolved *after* the reader had closed or
   * switched put the previous thread's attachment back up. The open thread is
   * held in a ref so the late resolution can ask what is open *now* rather than
   * what was open when it started.
   */
  const openThreadRef = useRef<string | null>(null);
  useEffect(() => {
    openThreadRef.current = openThreadId;
    if (openThreadId === null) setPreview(null);
  }, [openThreadId]);

  const openAttachment = useCallback(
    async (attachment: { id: string; filename: string; contentType?: string | null }) => {
      if (openThreadId === null) return;
      const forThread = openThreadId;
      const url = await fetchAttachmentUrl(forThread, attachment.id);
      // Nothing to show for a conversation the reader has already left.
      if (url === null || openThreadRef.current !== forThread) return;
      setPreview({
        id: attachment.id,
        fileName: attachment.filename,
        mimeType: attachment.contentType ?? '',
        downloadUrl: url,
      });
    },
    [openThreadId],
  );

  /**
   * Find this record's conversation, or start one.
   *
   * Only on click, and only for the row somebody actually wants — which is the
   * whole point of taking `count` from the page instead of asking per row.
   *
   * ⚠️ **The lookup is by record, not `threads({ entityId })`.** That list is
   * scoped to the caller's own memberships, so a producer left off an enquiry's
   * participants would see none and this would start a *second* conversation on
   * an enquiry already being answered.
   */
  async function openConversation() {
    setOpening(true);
    try {
      const existing = await fetchRecordThreadId(entityType, entityId);
      if (existing) {
        setOpenThreadId(existing);
        return;
      }
      const created = await transport.startThread?.({ entityType, entityId });
      if (created?.id) setOpenThreadId(created.id);
    } finally {
      setOpening(false);
    }
  }

  /**
   * The record's other conversations, and the way to open one more.
   *
   * ⚠️ **The list appears only when there is a choice to make.** One
   * conversation and a dropdown listing it is a control that does nothing;
   * "New conversation" is always offered, because being unable to start a
   * second one is the thing being fixed.
   */
  function switcher() {
    return (
      <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="conversation-switcher">
        {threads.length > 1 && (
          <select
            value={openThreadId ?? ''}
            onChange={(event) => setOpenThreadId(event.target.value)}
            className="max-w-full rounded-lg border border-gray-200 px-2 py-1 text-xs text-gray-700"
            aria-label="Which conversation"
            data-testid="conversation-switcher-select"
          >
            {threads.map((thread) => (
              <option key={thread.id} value={thread.id}>
                {conversationLabel(thread)}
              </option>
            ))}
          </select>
        )}
        <button
          type="button"
          onClick={() => setNewOpen(true)}
          className="flex items-center gap-1 rounded-lg border border-dashed border-gray-200 px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50"
          data-testid="new-conversation"
        >
          <Plus size={13} />
          New conversation
        </button>
        {nextOffset !== null && (
          <button
            type="button"
            disabled={loadingMore}
            onClick={() => {
              setLoadingMore(true);
              void loadPage(nextOffset).finally(() => setLoadingMore(false));
            }}
            className="rounded-lg border border-gray-200 px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-60"
            data-testid="conversation-switcher-more"
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        )}
      </div>
    );
  }

  /**
   * The attachment viewer.
   *
   * ⚠️ **A function, because this component returns from two places.** It was
   * written once, into the last `return` — and the modal path returns before
   * that, so opening a conversation from a record fetched the file, set the
   * state and rendered nothing at all. Clicking an attachment simply did
   * nothing, which is a hard symptom to read. `newConversation()` is a function
   * for the same reason; anything both paths need has to be one.
   */
  function attachmentPreview() {
    return preview === null ? null : (
      <DocumentPreviewModal document={preview} onClose={() => setPreview(null)} />
    );
  }

  function newConversation() {
    return (
      <NewConversationModal
        open={newOpen}
        onClose={() => setNewOpen(false)}
        scope={{ entityType, entityId }}
        onCreated={(threadId) => {
          setOpenThreadId(threadId);
          loadThreads();
        }}
      />
    );
  }

  // The panel needs to know who is reading it — to mark the right person's
  // messages read, and to tell "you" from everybody else in the transcript.
  if (!user) return null;

  /*
   * ⚠️ **The icon is never hidden, even at zero.** The reference implementation
   * renders nothing when a record has no thread and the reader may not start
   * one — right there, because most of its rows are read-only to most people.
   * Here every producer may start one, so hiding the control at zero would
   * remove the only way to begin a conversation from a list. It is dimmed
   * instead: present, quiet, and the one thing on the row that changes colour
   * when somebody is waiting on a reply.
   */
  if (variant === 'icon') {
    const total = count?.total ?? 0;
    const unread = count?.unread ?? 0;
    const tone =
      unread > 0
        ? 'text-primary-600 hover:bg-primary-50'
        : total > 0
          ? 'text-gray-500 hover:bg-gray-100'
          : 'text-gray-300 hover:bg-gray-100 hover:text-gray-500';

    return (
      <span className="peles-controls" data-testid="record-conversations">
        <button
          type="button"
          onClick={(event) => {
            // The row underneath is usually clickable — opening a conversation
            // must not also open the record behind it.
            event.preventDefault();
            event.stopPropagation();
            void openConversation();
          }}
          disabled={opening}
          className={`relative rounded p-1 transition-colors disabled:opacity-50 ${tone}`}
          title={
            total > 0 ? `${total} conversation${total === 1 ? '' : 's'}` : 'Start a conversation'
          }
          aria-label={total > 0 ? `Conversations (${total})` : 'Start a conversation'}
          data-testid="record-conversations-open"
        >
          <MessageSquare size={15} />
          {unread > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[14px] h-[14px] px-1 rounded-full bg-primary-600 text-white text-[9px] leading-[14px] font-medium"
              data-testid="record-conversations-count"
            >
              {unread}
            </span>
          )}
        </button>

        {/*
          ⚠️ **`.peles-controls` again, inside.** `EntityModal` renders through a
          portal to `<body>`, so the wrapper on the span above is not an
          ancestor of anything in the modal — the palette bridge stopped at the
          icon and the panel inside came out in the package's own slate and
          blue, beside an application that is gray and purple. The inline
          variant below never had the problem because nothing portals.
        */}
        <EntityModal
          open={openThreadId !== null}
          onClose={() => setOpenThreadId(null)}
          title="Conversation"
          size="lg"
        >
          {openThreadId && user && (
            <div className="peles-controls">
              {switcher()}
              <ThreadPanel
                key={reloadKey}
                transport={transport}
                threadId={openThreadId}
                viewer={{ id: user.id }}
                onCheckGrammar={async (body) => (await checkGrammar(body)).corrected}
                onOpenAttachment={(attachment) => void openAttachment(attachment)}
              />
              <ThreadTools
                threadId={openThreadId}
                context={context}
                onChanged={() => setReloadKey((key) => key + 1)}
                onStageUpload={setStagedUpload}
              />
            </div>
          )}
        </EntityModal>
        {newConversation()}
        {attachmentPreview()}
      </span>
    );
  }

  return (
    <div className="peles-controls" data-testid="record-conversations">
      {count === undefined ? (
        <InquiryButton
          transport={transport}
          entityType={entityType}
          entityId={entityId}
          {...(label ? { label } : {})}
          onOpen={(threadId) => setOpenThreadId(threadId)}
        />
      ) : (
        <button
          type="button"
          onClick={() => void openConversation()}
          disabled={opening}
          className="flex items-center gap-1.5 px-2.5 py-1 text-xs text-primary-700 border border-primary-200 rounded-md hover:bg-primary-50 disabled:opacity-50"
          data-testid="record-conversations-open"
        >
          <MessageSquare size={13} />
          {label ?? 'Conversation'}
          {count !== null && count.total > 0 && (
            <span
              className={`px-1.5 rounded-full ${
                count.unread > 0 ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600'
              }`}
              data-testid="record-conversations-count"
            >
              {count.unread > 0 ? count.unread : count.total}
            </span>
          )}
        </button>
      )}

      {openThreadId && (
        <div className="mt-3">
          {switcher()}
          <ThreadPanel
            key={reloadKey}
            transport={transport}
            threadId={openThreadId}
            viewer={{ id: user.id }}
            onCheckGrammar={async (body) => (await checkGrammar(body)).corrected}
            onOpenAttachment={(attachment) => void openAttachment(attachment)}
            onClose={() => setOpenThreadId(null)}
          />
          <ThreadTools
            threadId={openThreadId}
            context={context}
            onChanged={() => setReloadKey((key) => key + 1)}
            onStageUpload={setStagedUpload}
          />
        </div>
      )}
      {newConversation()}
      {attachmentPreview()}
    </div>
  );
}

/**
 * What one conversation is called in the switcher.
 *
 * Its subject if it has one, otherwise who is on it — "Bella Catering, Dina
 * Katz" tells the producer which conversation this is, and three rows reading
 * "Conversation" tell them nothing.
 */
function conversationLabel(thread: RecordThread): string {
  const subject = thread.subject?.trim();
  if (subject) return subject;
  const names = thread.participants
    .map((participant) => participant.name)
    .filter((name): name is string => Boolean(name));
  return names.length > 0 ? names.join(', ') : 'Conversation';
}
